import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Car, MapPin, User, RefreshCw } from "lucide-react";
import L from "leaflet";

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

interface VehicleMapData {
  id: number;
  plate_number: string;
  name?: string;
  brand?: string;
  model?: string;
  an_fabricatie?: number;
  type?: string;
  status?: string;
  assigned_employee_id?: number;
  angajat_asignat?: string;
  employee_role?: string;
  has_assigned_employee: boolean;
  coordinates: {
    lat: number;
    lng: number;
    address: string;
    timestamp?: string;
  };
  vehicle_full_name: string;
  display_name: string;
}

interface MapStatistics {
  total: number;
  assigned: number;
  unassigned: number;
}

// Simple vehicle icon
const createVehicleIcon = (vehicle: VehicleMapData) => {
  const color = vehicle.has_assigned_employee ? '#10b981' : '#6b7280';
  
  const html = `
    <div style="
      background: ${color};
      border: 2px solid white;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    "></div>
    <div style="
      background: #1f2937;
      color: white;
      padding: 1px 4px;
      border-radius: 2px;
      font-size: 8px;
      font-weight: bold;
      margin-top: 2px;
      text-align: center;
      white-space: nowrap;
    ">${vehicle.plate_number}</div>
  `;
  
  return L.divIcon({
    html,
    className: 'simple-marker',
    iconSize: [60, 30],
    iconAnchor: [30, 30],
    popupAnchor: [0, -30]
  });
};

const AllMap: React.FC = () => {
  const [vehicles, setVehicles] = useState<VehicleMapData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<MapStatistics | null>(null);

  useEffect(() => {
    fetchVehiclesMapData();
  }, []);

  const fetchVehiclesMapData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🗺️ Fetching vehicles map data...');
      const response = await fetch(`${API_BASE_URL}/api/vehicles/map-data`);
      const result = await response.json();
      
      if (result.success) {
        setVehicles(result.data);
        setStatistics(result.statistics);
        console.log(`✅ Loaded ${result.data.length} vehicles for map`);
      } else {
        throw new Error(result.error || 'Eroare la încărcarea datelor');
      }
    } catch (err) {
      console.error('❌ Error fetching map data:', err);
      setError(err instanceof Error ? err.message : 'Eroare necunoscută');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'Necunoscut';
    try {
      return new Date(timestamp).toLocaleString('ro-RO');
    } catch {
      return 'Format invalid';
    }
  };

  const romaniaCenter: [number, number] = [45.9432, 24.9668];

  return (
    <div className="w-full h-screen flex flex-col bg-white">
      {/* Simple CSS for markers */}
      <style>{`
        .simple-marker {
          background: transparent !important;
          border: none !important;
          text-align: center;
          font-family: system-ui, -apple-system, sans-serif;
        }
      `}</style>

      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5 text-blue-600" />
            <div>
              <h1 className="text-lg font-bold text-gray-800">Harta Vehiculelor</h1>
              {statistics && (
                <p className="text-sm text-gray-600">
                  Total: {statistics.total} • Asignate: {statistics.assigned} • Neasignate: {statistics.unassigned}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={fetchVehiclesMapData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Reîmprospătează
          </button>
        </div>
      </div>

      {/* Map Content */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
              <p className="text-gray-600">Se încarcă vehiculele...</p>
            </div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="text-center text-red-600">
              <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium mb-2">Eroare la încărcare</p>
              <p className="text-sm">{error}</p>
              <button
                onClick={fetchVehiclesMapData}
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Încearcă din nou
              </button>
            </div>
          </div>
        ) : (
          <MapContainer 
            center={romaniaCenter}
            zoom={7}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            {/* Simple OpenStreetMap tiles - most lightweight option */}
            <TileLayer 
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap"
              maxZoom={18}
            />
            
            {/* Render vehicle markers */}
            {vehicles.map((vehicle) => (
              <Marker 
                key={vehicle.id}
                position={[vehicle.coordinates.lat, vehicle.coordinates.lng]} 
                icon={createVehicleIcon(vehicle)}
              >
                <Popup>
                  <div className="min-w-[180px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Car className="w-4 h-4 text-blue-500" />
                      <strong>{vehicle.vehicle_full_name}</strong>
                    </div>
                    
                    <div className="text-sm space-y-1">
                      <p><strong>Număr:</strong> {vehicle.plate_number}</p>
                      {vehicle.an_fabricatie && (
                        <p><strong>An:</strong> {vehicle.an_fabricatie}</p>
                      )}
                      
                      {vehicle.has_assigned_employee ? (
                        <div className="flex items-center gap-1 text-green-700 bg-green-50 p-1 rounded">
                          <User className="w-3 h-3" />
                          <span className="text-xs">{vehicle.angajat_asignat}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-gray-500">
                          <User className="w-3 h-3" />
                          <span className="text-xs">Neasignat</span>
                        </div>
                      )}
                      
                      <div className="mt-2 pt-2 border-t text-xs text-gray-500">
                        {vehicle.coordinates.timestamp && (
                          <p>{formatTimestamp(vehicle.coordinates.timestamp)}</p>
                        )}
                        <div className="flex items-start gap-1 mt-1">
                          <MapPin className="w-3 h-3 mt-0.5 text-red-500 flex-shrink-0" />
                          <span className="break-words">{vehicle.coordinates.address}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}

        {/* No vehicles message */}
        {!loading && !error && vehicles.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
            <div className="text-center text-gray-500">
              <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nu sunt vehicule de afișat</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllMap;